<template>
  <v-container fluid class="ebf-page pa-2 pa-md-4">
    <SmartModuleDashboard module-key="employee-benefit-funds" />
    <v-divider class="my-5"/>
    <FundPageHeader
      title="Employee Benefit Funds"
      subtitle="Separate PF, gratuity, employee loan and benefit claim control centre"
      icon="mdi-account-cash-outline"
    >
      <v-btn color="white" variant="tonal" prepend-icon="mdi-refresh" :loading="loading" @click="loadDashboard">
        Refresh
      </v-btn>
    </FundPageHeader>

    <v-alert v-if="error" type="error" variant="tonal" class="mb-4" closable>{{ error }}</v-alert>

    <v-row>
      <v-col v-for="metric in metrics" :key="metric.label" cols="12" sm="6" lg="4">
        <MetricCard v-bind="metric" />
      </v-col>
    </v-row>

    <v-row class="mt-1">
      <v-col cols="12" lg="7">
        <v-card class="ebf-section-card" elevation="0">
          <v-card-title class="d-flex align-center ga-2">
            <v-icon color="primary">mdi-bank-transfer</v-icon>
            Fund workflow
          </v-card-title>
          <v-divider />
          <v-list lines="two">
            <v-list-item
              title="Payroll transfers"
              subtitle="Review payroll-generated PF and loan recovery journals, then record fund receipts."
              prepend-icon="mdi-swap-horizontal-bold"
              to="/employee-benefit-funds/payroll-transfers"
            />
            <v-list-item
              title="Gratuity deposits"
              subtitle="Post variable monthly bank deposits directly to the separate gratuity fund."
              prepend-icon="mdi-bank-plus"
              to="/employee-benefit-funds/gratuity-deposits"
            />
            <v-list-item
              title="Benefit claims"
              subtitle="Submit, review, approve and pay PF, gratuity and medical claims."
              prepend-icon="mdi-file-document-check-outline"
              to="/employee-benefit-funds/claims"
            />
          </v-list>
        </v-card>
      </v-col>

      <v-col cols="12" lg="5">
        <v-card class="ebf-section-card" elevation="0">
          <v-card-title class="d-flex align-center ga-2">
            <v-icon color="primary">mdi-chart-box-outline</v-icon>
            Quick reports
          </v-card-title>
          <v-divider />
          <v-card-text class="d-flex flex-wrap ga-2">
            <v-btn variant="tonal" color="primary" to="/employee-benefit-funds/reports/fund-summary">Fund summary</v-btn>
            <v-btn variant="tonal" color="primary" to="/employee-benefit-funds/reports/pf-contributions">PF contributions</v-btn>
            <v-btn variant="tonal" color="primary" to="/employee-benefit-funds/reports/gratuity-fund">Gratuity fund</v-btn>
            <v-btn variant="tonal" color="primary" to="/employee-benefit-funds/reports/trial-balance">Trial balance</v-btn>
            <v-btn variant="tonal" color="primary" to="/employee-benefit-funds/reports/employee-statement">Employee statement</v-btn>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-overlay :model-value="loading" contained class="align-center justify-center">
      <v-progress-circular indeterminate color="primary" size="52" />
    </v-overlay>
  </v-container>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import SmartModuleDashboard from "@/components/module/smart/SmartModuleDashboard.vue";
import { toast } from "vue3-toastify";
import employeeBenefitFundsApi from "@/services/employeeBenefitFundsApi";
import FundPageHeader from "./components/FundPageHeader.vue";
import MetricCard from "./components/MetricCard.vue";
import { apiError, envelopeData, money } from "./utils";

const loading = ref(false);
const error = ref("");
const dashboard = ref({});

const metrics = computed(() => [
  { label: "PF Cash Balance", value: money(dashboard.value.pf_cash_balance), icon: "mdi-piggy-bank-outline", color: "teal" },
  { label: "Gratuity Cash Balance", value: money(dashboard.value.gratuity_cash_balance), icon: "mdi-bank-outline", color: "indigo" },
  { label: "PF Member Liability", value: money(dashboard.value.pf_member_liability), icon: "mdi-account-group-outline", color: "blue" },
  { label: "Loan Outstanding", value: money(dashboard.value.employee_loan_outstanding), icon: "mdi-hand-coin-outline", color: "orange" },
  { label: "Payroll Transfer Due", value: money(dashboard.value.payroll_transfer_outstanding), icon: "mdi-timer-sand", color: "deep-orange" },
  {
    label: "Claims Requiring Action",
    value: Number(dashboard.value.submitted_claims || 0) + Number(dashboard.value.approved_unpaid_claims || 0),
    hint: `${dashboard.value.submitted_claims || 0} submitted, ${dashboard.value.approved_unpaid_claims || 0} approved unpaid`,
    icon: "mdi-clipboard-alert-outline",
    color: "purple",
  },
]);

const loadDashboard = async () => {
  loading.value = true;
  error.value = "";
  try {
    dashboard.value = envelopeData(await employeeBenefitFundsApi.dashboard()) || {};
  } catch (exception) {
    error.value = apiError(exception, "Unable to load the fund dashboard.");
    toast.error(error.value);
  } finally {
    loading.value = false;
  }
};

onMounted(loadDashboard);
</script>
